import React, { useState } from 'react';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const FIELD_OPTIONS = {
  Sex: ['M', 'F'],
  ChestPainType: ['ATA', 'NAP', 'ASY', 'TA'],
  RestingECG: ['Normal', 'ST', 'LVH'],
  ExerciseAngina: ['Y', 'N'],
  ST_Slope: ['Up', 'Flat', 'Down'],
};

const FIELD_LABELS = {
  Age: 'Age (years)',
  Sex: 'Sex',
  ChestPainType: 'Chest Pain Type',
  RestingBP: 'Resting BP (mm Hg)',
  Cholesterol: 'Cholesterol (mm/dl)',
  FastingBS: 'Fasting Blood Sugar > 120',
  RestingECG: 'Resting ECG',
  MaxHR: 'Max Heart Rate',
  ExerciseAngina: 'Exercise Angina',
  Oldpeak: 'ST Depression (Oldpeak)',
  ST_Slope: 'ST Slope',
};

const CHEST_PAIN_LABELS = {
  TA: 'Typical Angina',
  ATA: 'Atypical Angina',
  NAP: 'Non-Anginal Pain',
  ASY: 'Asymptomatic',
};

const DEFAULT_PATIENT = {
  Age: 55,
  Sex: 'M',
  ChestPainType: 'ATA',
  RestingBP: 130,
  Cholesterol: 240,
  FastingBS: 0,
  RestingECG: 'Normal',
  MaxHR: 150,
  ExerciseAngina: 'N',
  Oldpeak: 1.0,
  ST_Slope: 'Up',
};

function RiskGauge({ score, level }) {
  const rotation = (score / 100) * 180 - 90;
  const color = level === 'Low' ? '#2ecc71' : level === 'Moderate' ? '#f39c12' : '#e74c3c';

  return (
    <div className="gauge-container">
      <svg viewBox="0 0 200 120" className="gauge-svg">
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="#e0e0e0" strokeWidth="12" strokeLinecap="round" />
        <path d="M 20 100 A 80 80 0 0 1 73 30" fill="none" stroke="#2ecc71" strokeWidth="12" strokeLinecap="round" opacity="0.3" />
        <path d="M 73 30 A 80 80 0 0 1 127 30" fill="none" stroke="#f39c12" strokeWidth="12" opacity="0.3" />
        <path d="M 127 30 A 80 80 0 0 1 180 100" fill="none" stroke="#e74c3c" strokeWidth="12" strokeLinecap="round" opacity="0.3" />
        <line
          x1="100" y1="100" x2="100" y2="30"
          stroke={color} strokeWidth="3" strokeLinecap="round"
          transform={`rotate(${rotation}, 100, 100)`}
        />
        <circle cx="100" cy="100" r="5" fill={color} />
      </svg>
      <div className="gauge-score" style={{ color }}>{score}%</div>
      <div className="gauge-level" style={{ color }}>{level} Risk</div>
    </div>
  );
}

function SHAPChart({ topFactors }) {
  if (!topFactors || topFactors.length === 0) return null;

  const maxAbs = Math.max(...topFactors.map(([, v]) => Math.abs(v)));

  return (
    <div className="shap-chart">
      <h3>Top Risk Factors (SHAP)</h3>
      <p className="shap-subtitle">
        Red bars increase risk, blue bars decrease risk
      </p>
      {topFactors.map(([feature, value]) => {
        const pct = (Math.abs(value) / maxAbs) * 100;
        const isPositive = value > 0;
        const label = feature.replace(/_/g, ' ').replace('ChestPainType ', 'Chest Pain: ')
          .replace('ST Slope ', 'ST Slope: ').replace('RestingECG ', 'ECG: ');
        return (
          <div key={feature} className="shap-bar-row">
            <span className="shap-feature">{label}</span>
            <div className="shap-bar-container">
              <div
                className={`shap-bar ${isPositive ? 'shap-positive' : 'shap-negative'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="shap-value">{value > 0 ? '+' : ''}{value.toFixed(3)}</span>
          </div>
        );
      })}
    </div>
  );
}

function App() {
  const [patient, setPatient] = useState(DEFAULT_PATIENT);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => {
    setPatient(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...patient,
        Age: parseInt(patient.Age),
        RestingBP: parseInt(patient.RestingBP),
        Cholesterol: parseInt(patient.Cholesterol),
        FastingBS: parseInt(patient.FastingBS),
        MaxHR: parseInt(patient.MaxHR),
        Oldpeak: parseFloat(patient.Oldpeak),
      };
      const res = await fetch(`${API_URL}/predict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Prediction failed');
      }
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app">
      <header className="header">
        <h1>Heart Disease Risk Predictor</h1>
        <p>Enter patient vitals to receive a risk assessment with AI-powered explainability</p>
      </header>

      <main className="main">
        <form className="patient-form" onSubmit={handleSubmit}>
          <h2>Patient Information</h2>

          <div className="form-grid">
            <div className="form-group">
              <label>{FIELD_LABELS.Age}</label>
              <input type="number" value={patient.Age} min={1} max={120}
                onChange={e => handleChange('Age', e.target.value)} />
            </div>

            <div className="form-group">
              <label>{FIELD_LABELS.Sex}</label>
              <select value={patient.Sex} onChange={e => handleChange('Sex', e.target.value)}>
                {FIELD_OPTIONS.Sex.map(v => <option key={v} value={v}>{v === 'M' ? 'Male' : 'Female'}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>{FIELD_LABELS.ChestPainType}</label>
              <select value={patient.ChestPainType}
                onChange={e => handleChange('ChestPainType', e.target.value)}>
                {FIELD_OPTIONS.ChestPainType.map(v =>
                  <option key={v} value={v}>{CHEST_PAIN_LABELS[v]}</option>
                )}
              </select>
            </div>

            <div className="form-group">
              <label>{FIELD_LABELS.RestingBP}</label>
              <input type="number" value={patient.RestingBP} min={0} max={300}
                onChange={e => handleChange('RestingBP', e.target.value)} />
            </div>

            <div className="form-group">
              <label>{FIELD_LABELS.Cholesterol}</label>
              <input type="number" value={patient.Cholesterol} min={0} max={700}
                onChange={e => handleChange('Cholesterol', e.target.value)} />
            </div>

            <div className="form-group">
              <label>{FIELD_LABELS.FastingBS}</label>
              <select value={patient.FastingBS}
                onChange={e => handleChange('FastingBS', e.target.value)}>
                <option value={0}>No (&lt; 120 mg/dl)</option>
                <option value={1}>Yes (&gt; 120 mg/dl)</option>
              </select>
            </div>

            <div className="form-group">
              <label>{FIELD_LABELS.RestingECG}</label>
              <select value={patient.RestingECG}
                onChange={e => handleChange('RestingECG', e.target.value)}>
                {FIELD_OPTIONS.RestingECG.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>{FIELD_LABELS.MaxHR}</label>
              <input type="number" value={patient.MaxHR} min={50} max={250}
                onChange={e => handleChange('MaxHR', e.target.value)} />
            </div>

            <div className="form-group">
              <label>{FIELD_LABELS.ExerciseAngina}</label>
              <select value={patient.ExerciseAngina}
                onChange={e => handleChange('ExerciseAngina', e.target.value)}>
                <option value="N">No</option>
                <option value="Y">Yes</option>
              </select>
            </div>

            <div className="form-group">
              <label>{FIELD_LABELS.Oldpeak}</label>
              <input type="number" value={patient.Oldpeak} min={-5} max={10} step={0.1}
                onChange={e => handleChange('Oldpeak', e.target.value)} />
            </div>

            <div className="form-group">
              <label>{FIELD_LABELS.ST_Slope}</label>
              <select value={patient.ST_Slope}
                onChange={e => handleChange('ST_Slope', e.target.value)}>
                {FIELD_OPTIONS.ST_Slope.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Analyzing...' : 'Assess Risk'}
          </button>
        </form>

        {error && <div className="error-message">Error: {error}</div>}

        {result && (
          <div className="results-panel">
            <h2>Risk Assessment</h2>
            <RiskGauge score={result.risk_score} level={result.risk_level} />
            <div className="result-details">
              <div className="detail-item">
                <span className="detail-label">Probability</span>
                <span className="detail-value">{(result.probability * 100).toFixed(1)}%</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Classification</span>
                <span className={`detail-value ${result.prediction === 1 ? 'positive' : 'negative'}`}>
                  {result.prediction === 1 ? 'Disease Indicated' : 'No Disease Indicated'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Decision Threshold</span>
                <span className="detail-value">{(result.threshold * 100).toFixed(1)}%</span>
              </div>
            </div>
            <SHAPChart topFactors={result.top_risk_factors} />
            <p className="disclaimer">
              This tool is for educational and demonstration purposes only. It is not a substitute for professional medical diagnosis or advice.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
